import { useState, useEffect } from 'react';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import { useToast } from '@/components/base/Toast';
import { useAuth } from '@/hooks/useAuth';

const API_URL = 'https://api.airmex.mx/api/v1';

function getToken() {
  return localStorage.getItem('airmex_token') || sessionStorage.getItem('airmex_token');
}

async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...((options.headers as Record<string, string>) || {}),
  };
  const res = await fetch(`${API_URL}${url}`, { ...options, headers });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: 'Error desconocido' }));
    throw new Error(error.detail || 'Error en la petición');
  }
  return res.json();
}

export default function ConfiguracionPage() {
  const { showToast } = useToast();
  const { usuario } = useAuth();
  const [condiciones, setCondiciones] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchWithAuth('/configuraciones/condiciones')
      .then(data => setCondiciones(data.condiciones_generales || ''))
      .catch(err => showToast('error', 'Error al cargar condiciones', err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!condiciones.trim()) {
      showToast('warning', 'Condiciones vacías', 'Se guardará un texto vacío.');
    }
    setSaving(true);
    try {
      await fetchWithAuth('/configuraciones/condiciones', {
        method: 'PUT',
        body: JSON.stringify({ condiciones_generales: condiciones }),
      });
      showToast('success', 'Condiciones actualizadas');
    } catch (err: any) {
      showToast('error', 'Error al guardar', err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <i className="ri-loader-4-line animate-spin text-2xl" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="font-heading text-xl font-semibold text-foreground-900">Condiciones Generales para Cotizaciones</h1>
          <p className="text-sm text-foreground-500 mt-1">Estas condiciones aparecerán al final del PDF de cada cotización.</p>
        </div>
        <div className="bg-white rounded-xl border border-background-200 p-6">
          <label className="block text-sm font-semibold text-foreground-700 mb-2">Texto de las condiciones</label>
          <textarea
            value={condiciones}
            onChange={(e) => setCondiciones(e.target.value)}
            rows={15}
            className="w-full px-4 py-3 rounded-xl border border-background-200 bg-white text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 resize-none"
            placeholder="Ej: 1. Validez de la cotización..."
          />
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50"
            >
              {saving ? 'Guardando...' : 'Guardar condiciones'}
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}